import classNames from "classnames";
import { capitalize } from "lodash";
import { useCallback } from "react";
import Count from "~/components/build/Show/components/Count";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import {
  TreeNode,
  TreeNodeRenderProps,
  TreeNodeType,
  createStepNode,
} from "~/components/build/Show/components/Sidebar/TreeNode";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { Build } from "~/components/build/Show/lib/types";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { useStepsForStateQuery } from "~/components/build/Show/lib/useSteps";
import Icon from "~/components/shared/Icon";
import { FilterByOption } from "~/components/build/Show/components/Sidebar/useFilterStore";

interface Props extends TreeNodeRenderProps {
  state: FilterByOption;
  count: number;
}

export function StateHeaderItem({ state, count, ...props }: Props) {
  const { build } = useBuild();
  const stepsGroupedByState = useBuildStore((state) => state.stepsGroupedByState);
  const setExpandedStates = useBuildPreferencesStore((state) => state.setExpandedStates);
  const expandedStates = useBuildPreferencesStore((state) => state.expandedStates || []);
  const query = useStepsForStateQuery(build, state, {
    enabled:
      props.collapsibleState === CollapsibleState.Expanded ||
      props.collapsibleState === CollapsibleState.Full,
  });

  const toggleState = useCallback(() => {
    expandedStates.find((expandedState) => expandedState === state)
      ? setExpandedStates(expandedStates.filter((expandedState) => expandedState !== state))
      : setExpandedStates([...expandedStates, state]);
  }, [expandedStates, setExpandedStates, state]);

  return (
    <button
      onClick={toggleState}
      data-testid={`${state}-group`}
      className={classNames(
        "flex h-7 w-full cursor-pointer items-center rounded-md bg-white py-1 pl-2 pr-1 hover:bg-purple-100 dark:hover:bg-purple-700/25",
        {
          // Since it's possible the store was populated by the fetchAllQuery, show loading state only when
          // we're doing the initial fetch and the store is currently empty.
          "animate-none": query.isLoading && !stepsGroupedByState.get(state)?.length,
        },
      )}
    >
      <span className="text-charcoal-600 text-xs/5 font-normal uppercase tracking-wide">
        {capitalize(state)}
      </span>

      <div className="flex w-7 shrink-0 items-center justify-center">
        <Icon
          icon="heroicons/outline/chevron-right"
          className="h-3 w-3 transition-transform duration-150 group-aria-expanded:rotate-90"
        />
      </div>

      <span className="ml-auto">
        <Count data-testid={`${state}-group-count`}>{count}</Count>
      </span>
    </button>
  );
}

export const createStateHeaderNode = (
  state: FilterByOption,
  count: number,
  build: Build,
): TreeNode => {
  return {
    id: state,
    type: TreeNodeType.StateHeader,
    render: (props) => <StateHeaderItem state={state} count={count} {...props} />,
    childNodes: (collapsibleState: CollapsibleState) => {
      if (collapsibleState === CollapsibleState.Expanded) {
        const steps = useBuildStore.getState().stepsGroupedByState.get(state) || [];

        return steps.map((step) => createStepNode(step, build));
      }
      return [];
    },
  };
};
